import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { NoticeIconList, NoticeIconModule, NoticeItem } from '@delon/abc/notice-icon';
import { DynamicFieldModel, DynamicFormComponent } from '@share';
import { formatDistanceToNow, parse } from 'date-fns';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';
import { PostService } from 'src/app/pages/post/post.service';
import { SubcategoryService } from 'src/app/pages/subcategory/subcategory.service';
import { FirebaseMessagingService } from 'src/app/share/services/firebase-messaging.service';
import { LoadingService } from 'src/app/share/services/loading.service';
import { NotiService } from 'src/app/share/services/noti.service';

@Component({
  selector: 'header-notify',
  template: `
    <notice-icon
      [data]="data"
      [count]="count"
      [loading]="loading"
      btnClass="alain-default__nav-item"
      btnIconClass="alain-default__nav-item-icon"
      (select)="select($event)"
      (clear)="clear($event)"
      (popoverVisibleChange)="loadData()"
    />
  `,
  styles: [`
  ::ng-deep.notice-icon__clear
  {
    display:none !important;
  }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoticeIconModule]
})
export class HeaderNotifyComponent implements OnInit, OnDestroy {
  private readonly nzI18n = inject(NzI18nService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NzModalService);
  private readonly postService = inject(PostService);
  private readonly subcategoryService = inject(SubcategoryService);
  private readonly notiService = inject(NotiService);
  private readonly loadingService = inject(LoadingService);
  private readonly firebaseMessagingService = inject(FirebaseMessagingService);

  private fcmSubscription?: Subscription;
  private notiCountSubscription?: Subscription;

  data: NoticeItem[] = [
    {
      title: 'User Post',
      list: [],
      emptyText: 'There is nothing to show!',
    },
    {
      title: 'Biz Post',
      list: [],
      emptyText: 'There is nothing to show!',
    }
  ];
  count = 0;
  loading = false;
  groupOptions: { text: string; value: string }[] = [];

  private buildFieldConfig(categoryMatchType: string): DynamicFieldModel[] {
    return [
      {
        name: 'image',
        type: 'image-file',
        label: 'Image',
        readonly: false,
        required: false
      },
      {
        label: 'Group Name',
        name: 'group_id',
        type: 'select',
        required: true,
        placeholder: 'Group Name',
        options: this.groupOptions,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Group Name required',
          },
        ],
      },
      {
        label: 'Main Category Name',
        name: 'maincategory_id',
        type: 'select',
        required: true,
        placeholder: 'Main Category Name',
        dependOn: 'group_id',
        matchType: categoryMatchType,
        optionUrl: 'categoryhierarchy/option',
        options: [],
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Main Category Name required',
          },
        ],
      },
      {
        label: 'id',
        name: 'id',
        type: 'hidden',
        placeholder: ''
      },
      {
        label: 'Post Type',
        name: 'type',
        type: 'hidden',
        value: categoryMatchType
      },
      {
        label: 'Status',
        name: 'status',
        type: 'select',
        class: 'col-md-6',
        placeholder: 'Select Status',
        required: true,
        options: [
          { value: 'draft', text: 'Draft' },
          { value: 'published', text: 'Published' },
        ],
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Status required',
          },
        ],
      },
    ];
  }

  private getCategoryMatchType(post: any, notificationType?: string): string {
    if (notificationType === 'Admin Post' || post?.createdType === 'admin') {
      return 'Admin Post';
    }
    if (notificationType === 'Biz Post' || post?.type === 'Biz Post') {
      return 'Biz Post';
    }
    return 'User Post';
  }

  ngOnInit(): void {
    this.notiCountSubscription = this.notiService.noti$.subscribe(count => {
      this.count = count;
      this.cdr.detectChanges();
    });
    this.fcmSubscription = this.firebaseMessagingService.currentFCMMessage.subscribe(() => {
      this.loadData();
    });
    this.subcategoryService.getGroupOption().subscribe(options => {
      this.groupOptions = options.map((item: any) => ({
        text: item.name,
        value: item.id
      }));
    });
  }

  private updateNoticeData(notices: NoticeIconList[]): NoticeItem[] {
    const data = this.data.slice();
    data.forEach(i => (i.list = []));

    notices.forEach(item => {
      const newItem = { ...item } as NoticeIconList;
      if (typeof newItem.datetime === 'string') {
        newItem.datetime = parse(newItem.datetime, 'yyyy-MM-dd', new Date());
      }
      if (newItem.datetime) {
        newItem.datetime = formatDistanceToNow(newItem.datetime as Date, { locale: this.nzI18n.getDateLocale() });
      }
      if (newItem.extra && newItem['status']) {
        newItem['color'] = (
          {
            todo: undefined,
            processing: 'blue',
            urgent: 'red',
            doing: 'gold'
          } as Record<string, string | undefined>
        )[newItem['status']];
      }
      data.find(w => w.title === newItem['type'])!.list.push(newItem);
    });
    return data;
  }

  loadData(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.notiService.getAdminNoti().subscribe({
      next: (x: any) => {
        const groupedNotifications = (x.data as any[]).reduce((acc, notification) => {
          const key = notification.notification_type;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(notification);
          return acc;
        }, {} as Record<string, any[]>);

        this.notiService.setNoti(x.totalCount);
        const userPost = groupedNotifications['User Post']?.map((item: any) => ({
          id: item.related_id,
          title: item.description,
          date_time: item.created_at,
          type: item.notification_type,
          _id: item.id
        })) || [];
        const bizPost = groupedNotifications['Biz Post']?.map((item: any) => ({
          id: item.related_id,
          title: item.description,
          date_time: item.created_at,
          type: item.notification_type,
          _id: item.id
        })) || [];
        this.data = this.updateNoticeData([...userPost, ...bizPost]);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  clear(type: string): void {
    console.log(type);
  }

  select(res: any): void {
    this.loadingService.showLoading();
    this.notiService.updateRead({ deleted_id: res.item._id }).subscribe({
      next: () => {
        this.postService.getItemById(res.item.id).subscribe({
          next: (response) => {
            const post = response.post ?? response;
            const categoryMatchType = this.getCategoryMatchType(post, res.item.type);
            const modalRef: NzModalRef = this.modalService.create({
              nzTitle: 'Post',
              nzContent: DynamicFormComponent,
              nzData: { fields: this.buildFieldConfig(categoryMatchType), data: post },
              nzFooter: null,
              nzMaskClosable: false
            });
            modalRef.updateConfig({
              nzFooter: [{
                label: 'Submit',
                type: 'primary',
                onClick: () => this.handleUpdate(modalRef)
              },
              {
                label: 'Cancel',
                onClick: () => this.handleCancel(modalRef)
              }]
            });
            this.loadingService.hideLoading();
          },
          error: () => this.loadingService.hideLoading()
        });
      },
      error: () => this.loadingService.hideLoading()
    });
  }

  handleCancel(modalRef: NzModalRef): void {
    const component = modalRef?.componentRef?.instance as DynamicFormComponent;
    component.expandedKeys = [];
    modalRef.destroy();
  }

  handleUpdate(modalRef: NzModalRef): void {
    const component = modalRef?.componentRef?.instance as DynamicFormComponent;
    component.updateValidity();
    if (component.form.valid) {
      component.expandedKeys = [];
      modalRef.destroy();
    } else {
      component.form.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.fcmSubscription?.unsubscribe();
    this.notiCountSubscription?.unsubscribe();
  }
}
